<template>
  <div>
    <v-card class="my-1">
      <v-card-title>Export To Bank</v-card-title>
      <v-row class="px-4">
        <v-col>
          <v-row class="px-4">
            <v-col>
              <v-autocomplete
                v-model="company"
                v-validate="'required'"
                label="Payroll Company"
                :loading="loading"
                :items="companies"
                item-text="name"
                item-value="id"
                :error-messages="errors.collect('company')"
                data-vv-name="company"
                data-vv-as="payroll company"
                @change="loadCompanyBanks"
              />
            </v-col>

            <v-col>
              <v-autocomplete
                v-model="defaultCompanyBank"
                v-validate="'required'"
                disabled
                label="Report Type"
                :loading="loading"
                :items="companyBanks"
                item-text="payroll_bank.name"
                item-value="payroll_bank.id"
                :error-messages="errors.collect('defaultCompanyBank')"
                data-vv-name="defaultCompanyBank"
                data-vv-as="report type"
              />
            </v-col>

            <v-col>
              <v-menu
                v-model="paymentDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="paymentDateDisplay"
                    v-validate="'required'"
                    label="Payment Date"
                    :error-messages="errors.collect('paymentDateDisplay')"
                    data-vv-name="paymentDateDisplay"  
                    data-vv-as="payment date"  
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="paymentDate"
                  @input="paymentDateMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>

          <v-row class="px-4">
            <v-col>
              <v-autocomplete
                v-model="payrollPeriod"
                v-validate="'required'"
                :loading="loading"
                label="Payroll Period"
                :items="payrollPeriods"
                item-value="id"
                item-text="payroll_date"
                clearable
                :error-messages="errors.collect('payrollPeriod')"
                data-vv-name="payrollPeriod"
                data-vv-as="payroll period"
                hide-selected
                @change="customFileName"
              />
            </v-col>

            <v-col>
              <v-autocomplete
                v-model="payrollCycle"
                v-validate="'required'"
                clearable
                label="Payroll Cycle"
                hide-selected
                :loading="loading"
                :items="filteredPayrollCycles"
                :error-messages="errors.collect('payrollCycle')"
                data-vv-name="payrollCycle"
                item-value="id"
                item-text="payroll_cycle.name"
                data-vv-as="payroll cycle"
              />
            </v-col>

            <v-col>
              <v-text-field
                v-model="filename"
                v-validate="'required'"
                label="Filename"
                :error-messages="errors.collect('filename')"  
                data-vv-name="filename"
                data-vv-as="filename"
              />
            </v-col>
          </v-row>
        </v-col>

        <v-col
          cols="2"
          class="ma-auto"
        >
          <v-row
            v-if="progressing" 
            class="text-center"
          >
            <v-col cols="12">
              <div>
                <span class="font-weight-bold">Downloading</span>
              </div>
              <v-progress-circular
                :rotate="360"
                :size="50"
                :width="5"
                :value="progressbar"
                color="teal"
              >
                {{ progressbar }}
              </v-progress-circular>
            </v-col>
          </v-row>

          <v-row class="text-center">
            <v-col cols="12">
              <v-btn
                class="mx-3"
                :loading="progressing"
                color="primary"
                @click="generate"
              >
                generate
              </v-btn>
            </v-col>
          </v-row> 
        </v-col>
      </v-row>
    </v-card> 
  </div>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    data () {
      return {
        loading: false,
        progressing: false,
        company: null,
        companies: [],
        progressbar: 0,
        companyBanks: [],
        payrollPeriods: [],
        payrollPeriod: null,
        filename: null,
        paymentDateMenu: false,
        paymentDate: null,
        payrollCycle: null,
        payrollCycles: [],
      }
    },

    computed: {
      defaultCompanyBank () {
        let selected = this.companyBanks.find(companyBank => companyBank.is_default === 1)

        if (selected) {
          return selected.payroll_bank_id
        }

        return null
      },
      selectedReportType () {
        if (!this.defaultCompanyBank) return null
        return this.companyBanks.find(companyBank => companyBank.payroll_bank.id === this.defaultCompanyBank)
      },

      paymentDateDisplay () {
        return this.paymentDate ? this.$moment(this.paymentDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },

      filteredPayrollCycles () {
        if (!this.payrollPeriod) return []

        return this.payrollPeriods.find(payrollPeriod => payrollPeriod.id === this.payrollPeriod).payroll_cycle_transactions
      },
    },

    created () {
      this.loading = true
        
      Promise.all([
        this.$api.hrisCompanies().list(),
        this.$api.payrollPeriods().list(),
      ])
        .then(([companies, payrollPeriods]) => {
          this.companies = _.orderBy(companies.data, ['name'], ['asc'])  
          this.payrollPeriods = _.orderBy(payrollPeriods.data, ['payroll_date'], ['desc'])  

          this.loading = false
        })
    },

    methods: {
      generate () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.exportToBanks().export(((this.selectedReportType || {}).payroll_bank || {}).bank_code.toLowerCase(), this.payrollPeriod, this.prepareFormData(), {
              onDownloadProgress: (progressEvent) => {
                this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
              },
            }).then(res => {
              let blob = new Blob([res.data], { type: res.data.type })
              let link = document.createElement('a')
              link.href = window.URL.createObjectURL(blob)
              link.setAttribute('download', this.filename + ' ' + ((res.data.type === 'text/plain') || (res.data.type === 'text/html') ? '.txt' : (res.data.type === 'text/csv') ? '.csv' : '.xlsx'))
              link.click()

              this.progressing = false
              this.progressbar = 0

              this.$snack({ message: 'Report downloaded successfully' })
            }).catch(() => {
              this.progressing = false

              this.$snack({ color: 'error', message: 'Error! Please contact support' })
            })
          })
      },

      loadCompanyBanks () {
        this.$api.companyBanks().show(this.company)
          .then(res => {
            this.companyBanks = _.orderBy(res.data, ['payroll_bank.name'], ['asc'])

            this.customFileName()
          })
      },

      prepareFormData () {
        return {
          company_uid: this.company,
          payment_date: this.paymentDate,
          file_name: this.filename,
          payroll_cycle_transaction_id: this.payrollCycle,
        }
      },

      customFileName () {
        if (this.company) {
          let selectedCompany = this.companies.find(company => company.id === this.company)
          let selectedFormat = this.companyBanks.find(companyBank => companyBank.payroll_bank.id === this.defaultCompanyBank)
          let selectedPayrollPeriod = this.payrollPeriods.find(payrollPeriod => payrollPeriod.id === this.payrollPeriod)

          let shortenCompanyName = (selectedCompany.name).split(' ').map(letter => letter.charAt(0)).join('')
          this.filename = shortenCompanyName + '-' + ((selectedFormat || {}).payroll_bank || {}).bank_code + '-' + (this.payrollPeriod ? selectedPayrollPeriod.payroll_date : '')
        }

        return null
      },
    },
  }
</script>
